import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import Request from '../../services/Request';
import * as XLSX from 'xlsx';

export interface MonthlyExportRow {
  clientCode: string;
  locationCode: string;
  locationName: string;
  category: string;
  classCode: string;
  className: string;
  turnPlan: number | null;
  turn: number | null;
  [key: string]: string | number | null;
}

interface Props {
  clientId: string | number;
  epochMonth: number;
}

const genSeq = (prefix: string, start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => `${prefix}${start + i}`);

const BASE_COLUMNS: string[] = [
  'clientCode',
  'locationCode',
  'locationName',
  'category',
  'classCode',
  'className',
  'turnPlan',
  'turn',
];

const SALES_PLAN_COLS = genSeq('salesPlan', 1, 12);
const MARKDOWNS_PLAN_COLS = genSeq('markdownsPlan', 1, 12);
const INFLOW_RETAIL_PLAN_COLS = genSeq('inflowRetailPlan', 1, 12);
const INFLOW_COST_PLAN_COLS = genSeq('inflowCostPlan', 1, 12);
const OTB_RETAIL_COLS = genSeq('otbRetail', 1, 12);
const OTB_COST_COLS = genSeq('otbCost', 1, 12);
const CURRENT_ACTUALS_COLS = ['sales1'];
const ON_ORDER_COST_COLS = genSeq('onOrderCost', 1, 6);
const INVENTORY_COLS = ['inventory1', 'inventoryDeviationCost1', 'inventoryDeviation1'];

const ALL_COLUMNS: string[] = [
  ...BASE_COLUMNS,
  ...SALES_PLAN_COLS,
  ...MARKDOWNS_PLAN_COLS,
  ...INFLOW_RETAIL_PLAN_COLS,
  ...INFLOW_COST_PLAN_COLS,
  ...OTB_RETAIL_COLS,
  ...OTB_COST_COLS,
  ...CURRENT_ACTUALS_COLS,
  ...ON_ORDER_COST_COLS,
  ...INVENTORY_COLS,
];

function formatCell(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (key === 'turn' || key === 'turnPlan') {
      const v = Number(value);
      if (!isFinite(v)) return '';
      return (Math.round(v * 10) / 10).toFixed(1);
    }
    const v = Math.round(Number(value));
    if (!isFinite(v)) return '';
    return String(v);
  }
  const num = Number(value as any);
  if (!isNaN(num)) {
    if (key === 'turn') {
      return (Math.round(num * 10) / 10).toFixed(1);
    }
    return String(Math.round(num));
  }
  return String(value);
}

const STICKY_META_COLS = ['clientCode', 'locationCode', 'locationName', 'category', 'classCode', 'className'];
const STICKY_COL_MIN_WIDTH = 140;
const STICKY_COL_MAX_WIDTH = 160;

// Allow smaller widths for specific metadata columns by fitting to header title width
const SMALL_STICKY_COLS = ['clientCode', 'locationCode', 'classCode'] as const;
const HEADER_PADDING_X_PX = 16; // tailwind px-2 on both sides ~ 16px total

function stickyWidthExpr(col: string): string {
  if (SMALL_STICKY_COLS.includes(col as any)) {
    // width equals header text length in ch + horizontal padding
    const headerLen = col.length;
    return `calc(${headerLen}ch + ${HEADER_PADDING_X_PX}px)`;
  }
  // default fixed width for other sticky columns
  return `${STICKY_COL_MAX_WIDTH}px`;
}

// Precompute left offsets for sticky columns using cumulative widths
const useStickyLeftOffsets = (): Record<string, string> => {
  const offsets: Record<string, string> = {};
  const parts: string[] = [];
  STICKY_META_COLS.forEach((c, i) => {
    offsets[c] = i === 0 ? '0px' : `calc(${parts.join(' + ')})`;
    parts.push(stickyWidthExpr(c));
  });
  return offsets;
};

const MonthlyExportReport: React.FC<Props> = ({ clientId, epochMonth }) => {
  const [isMtd, setIsMtd] = useState<boolean>(false);
  const [rows, setRows] = useState<MonthlyExportRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const columns = useMemo(() => ALL_COLUMNS, []);
  const stickyLeftOffsets = useMemo(() => useStickyLeftOffsets(), []);

  // Export helpers: dump the visible table verbatim using columns and rows
  function exportToXlsx() {
    const data = [columns, ...rows.map((r) => columns.map((c) => r[c] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MonthlyExport');
    XLSX.writeFile(wb, 'monthly_export.xlsx');
  }

  function exportToCsv() {
    const data = [columns, ...rows.map((r) => columns.map((c) => r[c] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'monthly_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      setRows([]);
      const params: any = { epochMonth, isMtd: isMtd ? 1 : 0 };
      const res = await Request.get(`/api/clients/${clientId}/reports/monthly-export`, params);
      const data = res?.data ?? res?.rows ?? res ?? [];
      if (Array.isArray(data)) {
        setRows(data as MonthlyExportRow[]);
      } else if (Array.isArray(res?.data?.data)) {
        setRows(res.data.data as MonthlyExportRow[]);
      } else {
        setRows([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [clientId, epochMonth, isMtd]);

  return (
    <Box sx={{ background: '#fff', padding: 2, borderRadius: 1, border: '1px solid #e5e7eb' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Monthly Export Report</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={isMtd} onChange={(e) => setIsMtd(e.target.checked)} />}
            label={isMtd ? 'MTD' : 'EOM'}
          />
          <button className="px-3 py-1 border rounded bg-white hover:bg-gray-50" onClick={exportToXlsx} disabled={rows.length === 0}>
            Export Excel
          </button>
          <button className="px-3 py-1 border rounded bg-white hover:bg-gray-50" onClick={exportToCsv} disabled={rows.length === 0}>
            Export CSV
          </button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}
      {error && !loading && (
        <Typography color="error" variant="body2">{error}</Typography>
      )}

      {!loading && !error && (
        <Box sx={{ width: '100%', maxWidth: 'calc(100vw - 175px)', maxHeight: 600, overflow: 'auto' }}>
          <table className="min-w-full border-collapse" style={{ fontSize: 12, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const isSticky = STICKY_META_COLS.includes(col);
                  const widthExpr = isSticky ? stickyWidthExpr(col) : undefined;
                  const leftExpr = isSticky ? stickyLeftOffsets[col] : undefined;
                  return (
                    <th
                      key={col}
                      className="border px-2 py-1 bg-gray-100 text-left whitespace-nowrap"
                      title={col}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: isSticky ? 10 : 2,
                        left: leftExpr,
                        minWidth: isSticky ? widthExpr : undefined,
                        width: isSticky ? widthExpr : undefined,
                        maxWidth: isSticky ? widthExpr : undefined,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        background: '#f3f4f6',
                        boxShadow: isSticky ? '2px 0 0 0 rgba(0,0,0,0.06)' : undefined,
                      }}
                    >
                      {col}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="border px-2 py-2 text-center text-gray-500">
                    No data
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {columns.map((c) => {
                      const isSticky = STICKY_META_COLS.includes(c);
                      const widthExpr = isSticky ? stickyWidthExpr(c) : undefined;
                      const leftExpr = isSticky ? stickyLeftOffsets[c] : undefined;
                      return (
                        <td
                          key={c}
                          className="border px-2 py-1 whitespace-nowrap"
                          style={{
                            position: isSticky ? 'sticky' as const : undefined,
                            left: leftExpr,
                            background: isSticky ? (i % 2 === 0 ? '#ffffff' : '#f9fafb') : undefined,
                            zIndex: isSticky ? 5 : undefined,
                            minWidth: isSticky ? widthExpr : undefined,
                            width: isSticky ? widthExpr : undefined,
                            maxWidth: isSticky ? widthExpr : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            boxShadow: isSticky ? '2px 0 0 0 rgba(0,0,0,0.06)' : undefined,
                          }}
                        >
                          {formatCell(c, r[c])}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

export default MonthlyExportReport;
